/**
 * <s-scratch-card>: navy scratch layer on a canvas over the prize copy; steps scratch -> form -> code (mirrored on the dialog data-step).
 * Scratching ~22% with a wide soft brush (plus falling navy flakes) or Enter/Space reveals the prize and a claim button; the claim button opens the email step.
 * The form subscribes the email to a Klaviyo list via Klaviyo's public client API (CORS-enabled, public site key only).
 */
const CLEAR_RATIO = 0.22;
// Brush radius scales with the card (about 1/9 of its width), never thinner than 26px.
const brushFor = (w) => Math.max(26, Math.round(w * 0.11));
const DUST = ["#2C3F66", "#3A4F7A", "#1B2A4A", "#55688F"];
const reduceMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

class ScratchCard extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector("[data-scratch-canvas]");
    this.form = this.querySelector("[data-scratch-form]");
    if (!this.canvas || !this.form) return;
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    this.dustCanvas = this.querySelector("[data-scratch-dust]");
    this.dustCtx = this.dustCanvas?.getContext("2d");
    this.flakes = [];
    this.brush = 30;
    this.ac = new AbortController();
    const opts = { signal: this.ac.signal };
    this.drawing = false;
    this.strokes = 0;

    // Paint once the card has a size (the dialog may still be closed on connect).
    this.ro = new ResizeObserver(() => {
      if (!this.revealed && this.canvas.clientWidth) this.#paint();
    });
    this.ro.observe(this.canvas);
    document.fonts?.load(`700 40px "STEAMRS Archivo"`).then(() => {
      if (!this.revealed && this.canvas.clientWidth && !this.strokes) this.#paint();
    }).catch(() => {});

    this.canvas.addEventListener("pointerdown", (e) => this.#start(e), opts);
    this.canvas.addEventListener("pointermove", (e) => this.#move(e), opts);
    ["pointerup", "pointercancel", "pointerleave"].forEach((t) => this.canvas.addEventListener(t, () => this.#end(), opts));
    this.canvas.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.#reveal();
        }
      },
      opts
    );
    this.form.addEventListener("submit", (e) => this.#submit(e), opts);
    this.querySelector("[data-scratch-claim]")?.addEventListener("click", () => this.#toForm(), opts);
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.ro?.disconnect();
    cancelAnimationFrame(this.raf);
  }

  #paint() {
    const { canvas, ctx } = this;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = "source-over";

    // STEAMRS navy, slightly lighter top-left like the reference card.
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#2B3B5C");
    grad.addColorStop(0.55, "#22314F");
    grad.addColorStop(1, "#1B2A4A");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Soft inner highlight along the top edge.
    const shine = ctx.createLinearGradient(0, 0, 0, h * 0.5);
    shine.addColorStop(0, "rgba(255,255,255,0.10)");
    shine.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = shine;
    ctx.fillRect(0, 0, w, h * 0.5);

    // "Kras teken": a pre-drawn scribble so it is obvious you can scratch here (drawn, not erased).
    // Fit the label (e.g. "HIER RUBBELN" is longer than "KRAS HIER") to ~82% of the card width.
    const label = this.dataset.scratchText || "";
    let fs = Math.round(Math.min(w / 7, 42));
    ctx.font = `700 ${fs}px "STEAMRS Archivo", Arial, sans-serif`;
    while (fs > 14 && ctx.measureText(label).width > w * 0.82) {
      fs -= 1;
      ctx.font = `700 ${fs}px "STEAMRS Archivo", Arial, sans-serif`;
    }
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "rgba(8,14,28,0.38)";
    ctx.lineWidth = fs * 0.62;
    ctx.filter = "blur(2px)";
    ctx.beginPath();
    const top = h / 2 - fs * 0.15;
    const bottom = h / 2 + fs * 0.75;
    const x0 = w * 0.3;
    const x1 = w * 0.62;
    const steps = 4;
    ctx.moveTo(x0, bottom);
    for (let i = 0; i < steps; i++) {
      const xa = x0 + ((x1 - x0) * (i + 0.5)) / steps;
      const xb = x0 + ((x1 - x0) * (i + 1)) / steps;
      ctx.lineTo(xa, top);
      ctx.lineTo(xb, bottom);
    }
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = "#C9D2E0";
    ctx.font = `700 ${fs}px "STEAMRS Archivo", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, w / 2, h / 2);
  }

  #point(e) {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  #start(e) {
    if (this.revealed) return;
    this.drawing = true;
    this.canvas.setPointerCapture?.(e.pointerId);
    this.last = this.#point(e);
    this.#scratch(this.last, this.last);
  }

  #move(e) {
    if (!this.drawing || this.revealed) return;
    // Coalesced events give every sampled point of a fast swipe, so the strip stays continuous.
    const events = e.getCoalescedEvents?.() || [];
    const list = events.length ? events : [e];
    for (const ev of list) {
      const p = this.#point(ev);
      this.#scratch(this.last, p);
      this.last = p;
    }
    if (++this.strokes % 6 === 0) this.#check();
  }

  // Little flakes of the navy layer falling off the brush, on a separate overlay canvas.
  #dust(a, b) {
    if (!this.dustCanvas || reduceMotion()) return;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.min(6, 1 + Math.floor(dist / 10));
    for (let i = 0; i < n; i++) {
      const t = Math.random();
      this.flakes.push({
        // +16: the dust overlay starts 1rem left/above the card.
        x: 16 + a.x + (b.x - a.x) * t + (Math.random() - 0.5) * this.brush,
        y: 16 + a.y + (b.y - a.y) * t + (Math.random() - 0.5) * this.brush * 0.6,
        vx: (Math.random() - 0.5) * 1.6,
        vy: -Math.random() * 1.2,
        s: 1.5 + Math.random() * 2.5,
        life: 1,
        c: DUST[(Math.random() * DUST.length) | 0],
      });
    }
    if (this.flakes.length > 160) this.flakes.splice(0, this.flakes.length - 160);
    if (!this.raf) this.raf = requestAnimationFrame(() => this.#tickDust());
  }

  #tickDust() {
    const c = this.dustCanvas;
    const ctx = this.dustCtx;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (c.width !== Math.round(w * dpr)) {
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    this.flakes = this.flakes.filter((f) => f.life > 0 && f.y < h + 10);
    for (const f of this.flakes) {
      f.vy += 0.18;
      f.x += f.vx;
      f.y += f.vy;
      f.life -= 0.022;
      ctx.globalAlpha = Math.max(0, f.life);
      ctx.fillStyle = f.c;
      ctx.fillRect(f.x, f.y, f.s, f.s * 0.8);
    }
    ctx.globalAlpha = 1;
    this.raf = this.flakes.length ? requestAnimationFrame(() => this.#tickDust()) : 0;
  }

  #end() {
    if (!this.drawing) return;
    this.drawing = false;
    this.#check();
  }

  #scratch(a, b) {
    const { ctx } = this;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    this.brush = brushFor(this.canvas.clientWidth);
    // Soft, slightly ragged edge: a wide blurred pass plus a solid core, so the strip looks scratched, not cut.
    ctx.shadowColor = "rgba(0,0,0,1)";
    ctx.shadowBlur = this.brush * 0.35;
    ctx.lineWidth = this.brush * 2;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x + 0.01, b.y);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.lineWidth = this.brush * 1.7;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x + 0.01, b.y);
    ctx.stroke();
    this.#dust(a, b);
  }

  #check() {
    const { width, height } = this.canvas;
    if (!width || !height) return;
    const data = this.ctx.getImageData(0, 0, width, height).data;
    let clear = 0;
    let total = 0;
    // Sample every 16th pixel's alpha; plenty for a threshold check.
    for (let i = 3; i < data.length; i += 64) {
      total++;
      if (data[i] < 128) clear++;
    }
    if (clear / total >= CLEAR_RATIO) this.#reveal();
  }

  #reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.drawing = false;
    this.canvas.setAttribute("data-cleared", "");
    this.setAttribute("data-revealed", "");
    navigator.vibrate?.(25);
    this.canvas.setAttribute("tabindex", "-1");
    this.canvas.setAttribute("aria-hidden", "true");
    const claim = this.querySelector("[data-scratch-claim]");
    if (!claim) return this.#toForm();
    claim.hidden = false;
    setTimeout(() => claim.focus({ preventScroll: true }), reduceMotion() ? 0 : 500);
  }

  // Mirror the step on the popup dialog so the shell can swap photo/layout; the intro copy shows only while scratching.
  #step(name) {
    const dialog = this.closest("dialog");
    if (!dialog) return;
    dialog.dataset.step = name;
    dialog.querySelectorAll("[data-scratch-intro]").forEach((el) => (el.hidden = name !== "scratch"));
  }

  #toForm() {
    this.querySelector("[data-scratch-stage]").hidden = true;
    this.form.hidden = false;
    this.#step("form");
    const input = this.form.querySelector('input[type="email"]');
    setTimeout(() => input?.focus({ preventScroll: true }), reduceMotion() ? 0 : 150);
  }

  // Apply the code to the cart/checkout straight away (Shopify's /discount/CODE sets it for the session) and wire the copy button.
  #applyCode() {
    const code = (this.dataset.code || "").trim();
    if (!code) return;
    const root = window.Shopify?.routes?.root || "/";
    // 1) /discount/CODE stores the code for the session, so checkout applies it even if the cart is still empty.
    // 2) Then put it on the cart itself the same way the cart drawer's own discount field does (s-cart.js),
    //    which also refreshes every mounted cart so the code shows in the drawer straight away.
    fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`, {
      credentials: "same-origin",
    })
      .catch(() => {})
      .then(() => window.Shopify?.actions?.updateCart?.({ discountCodes: [code] }, { event: { context: "cart" } }))
      .catch(() => {});
    const copy = this.querySelector("[data-scratch-copy]");
    copy?.addEventListener(
      "click",
      async () => {
        try {
          await navigator.clipboard.writeText(code);
          const note = this.querySelector("[data-scratch-copied]");
          if (note) note.hidden = false;
        } catch {}
      },
      { signal: this.ac.signal }
    );
  }

  async #submit(e) {
    e.preventDefault();
    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector("[data-scratch-submit]");
    const error = this.form.querySelector("[data-scratch-error]");
    const email = input.value.trim();
    error.hidden = true;
    if (!input.checkValidity() || !email) {
      error.hidden = false;
      input.focus();
      return;
    }

    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.sending || label;

    const body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Kraskaart pop-up",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
                properties: { popup_language: document.documentElement.lang || "" },
              },
            },
          },
        },
        relationships: { list: { data: { type: "list", id: this.dataset.list } } },
      },
    };

    try {
      const url = `${this.dataset.endpoint}?company_id=${encodeURIComponent(this.dataset.company)}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/vnd.api+json", revision: "2024-10-15" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(String(res.status));
      this.form.hidden = true;
      this.querySelector("[data-scratch-success]").hidden = false;
      const dismiss = this.querySelector("[data-scratch-dismiss]");
      if (dismiss) dismiss.hidden = true;
      this.#step("code");
      this.#applyCode();
      this.dispatchEvent(new CustomEvent("scratch:subscribed", { bubbles: true }));
    } catch (err) {
      error.hidden = false;
      button.disabled = false;
      button.textContent = label;
    }
  }
}

if (!customElements.get("s-scratch-card")) customElements.define("s-scratch-card", ScratchCard);
